import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../Components/AddRelationshipTopBar";
import { useHeader } from "../../Providers/HeaderContextProvider";

/**
 * Journey screens own their headers. The global HeaderComponent floats a
 * full-width transparent bar at z-index 1000 over the top of the page, which
 * swallows clicks aimed at anything rendered beneath it — including a back
 * button. Hiding it (and restoring on unmount) is what makes in-page
 * navigation clickable at all.
 */
export const useStandaloneHeader = () => {
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  useEffect(() => {
    setHeaderContent(false);
    setHeaderContentRightSideContent(false);
    return () => {
      setHeaderContent(true);
      setHeaderContentRightSideContent(true);
    };
  }, [setHeaderContent, setHeaderContentRightSideContent]);
};

/**
 * The app-standard page header: TopBar (AddRelationshipTopBar), same as
 * RelationshipProfile / Membership / Connections — white circular back arrow
 * hard-left, title centered. `to` is required — an explicit destination
 * survives refreshes and deep links, where history(-1) leaves the app.
 */
export const JourneyBackHeader = ({ to, title }) => {
  const navigate = useNavigate();
  return (
    <div className="journey-topbar">
      <TopBar
        title={title}
        showBackButton
        useRegularArrow
        onBackClick={() => navigate(to)}
      />
    </div>
  );
};

/**
 * The content of a Journey sub-page, below its TopBar. On a desktop it is the
 * white rounded card My Connections uses; on a phone it adds nothing.
 */
export const JourneySubpageCard = ({ children }) => (
  <div className="journey-subcard">{children}</div>
);

/* Inline card icons — SVG, never emoji: emoji glyphs render as ✕/tofu boxes
 * on systems without the relevant font, which reads as broken UI. */
export const FlameIcon = ({ color = "#eb7452" }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3s1 2.5-1 5c-1.2 1.5-2.5 2.6-2.5 4.5A3.5 3.5 0 0 0 12 16a3.5 3.5 0 0 0 3.5-3.5c0-.8-.2-1.5-.6-2.2.9.4 3.1 2 3.1 5.2A6 6 0 0 1 6 15.5C6 10.5 10.5 9 12 3z"
      fill={color}
    />
  </svg>
);

export const BoltIcon = ({ color = "#03a388" }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M13 2L4.5 13.5H11L9.5 22 19 10.5h-6.5L13 2z"
      fill={color}
    />
  </svg>
);

export const TrendIcon = ({ color = "#eb7452" }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 17l5-5 3 3 7-8"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M15 7h4v4" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const SparkIcon = ({ color = "#0d70dc" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"
      fill={color}
    />
  </svg>
);

export const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14m0 0l-6-6m6 6l-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MicIcon = ({ color = "#0d70dc" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="3" width="6" height="10" rx="3" fill={color} />
    <path
      d="M5 11a7 7 0 0 0 14 0M12 18v3"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const ChatIcon = ({ color = "#0d70dc" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 4v-4H6a2 2 0 0 1-2-2V6z"
      fill={color}
    />
  </svg>
);

export const BookIcon = ({ color = "#664bd6" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v15H6.5A2.5 2.5 0 0 0 4 20.5v-15z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M8 7.5h7" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const CheckIcon = ({ color = "#03a388" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12.5l4.5 4.5L19 7"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const StarIcon = ({ color = "#486bee" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.8L12 16.9l-5.3 2.7 1.1-5.8-4.3-4.1 5.9-.8L12 3.5z"
      fill={color}
    />
  </svg>
);

/**
 * Recent-Journey row glyph + tile colour, keyed by the icon family the
 * backend sends (`icon_for()` in aris-core — total, fallback `journey_event`).
 * Deliberately reuses the card glyphs the rest of the page already uses
 * (flame = Current Focus, bolt = Check-In, trend = momentum-ish, spark =
 * insight) so an event reads as the same thing as the card above it.
 */
const EVENT_ICONS = {
  voice_conversation: [MicIcon, "var(--journey-blue-bg)"],
  text_conversation: [ChatIcon, "var(--journey-blue-bg)"],
  guided_journey: [BookIcon, "var(--journey-crayola-bg)"],
  guided_dialogue: [BookIcon, "var(--journey-crayola-bg)"],
  daily_check_in: [BoltIcon, "var(--journey-positive-bg)"],
  reflection: [BookIcon, "var(--journey-crayola-bg)"],
  prayer: [SparkIcon, "var(--journey-crayola-bg)"],
  breathwork: [SparkIcon, "var(--journey-crayola-bg)"],
  current_focus: [FlameIcon, "var(--journey-accent-bg)"],
  commitment: [CheckIcon, "var(--journey-positive-bg)"],
  applied_in_life: [CheckIcon, "var(--journey-positive-bg)"],
  insight: [SparkIcon, "var(--journey-crayola-bg)"],
  assessment: [TrendIcon, "var(--journey-accent-bg)"],
  health: [TrendIcon, "var(--journey-accent-bg)"],
  major_life_event: [StarIcon, "var(--journey-crayola-bg)"],
  mission: [StarIcon, "var(--journey-crayola-bg)"],
  saved: [StarIcon, "var(--journey-crayola-bg)"],
  discovery: [SparkIcon, "var(--journey-crayola-bg)"],
  domain_unlocked: [StarIcon, "var(--journey-crayola-bg)"],
  element_unlocked: [StarIcon, "var(--journey-crayola-bg)"],
  foundation_complete: [StarIcon, "var(--journey-crayola-bg)"],
};

const FALLBACK_EVENT_ICON = [SparkIcon, "var(--journey-blue-bg)"];

export const JourneyEventIcon = ({ icon }) => {
  const [Glyph, tile] = EVENT_ICONS[icon] || FALLBACK_EVENT_ICON;
  return (
    <span className="journey-timeline__tile" style={{ background: tile }}>
      <Glyph />
    </span>
  );
};

/**
 * Parse a Journey `occurred_at`.
 *
 * core-lambda serialises the stored (naive, UTC) datetime with
 * `datetime.isoformat()`, which emits no timezone designator. `new Date()`
 * reads a designator-less ISO string as *local* time, which silently shifts
 * every timestamp by the viewer's offset — so UTC is asserted when the string
 * doesn't say otherwise. Returns null for anything unparseable.
 */
export const parseOccurredAt = (iso) => {
  if (!iso || typeof iso !== "string") return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const date = new Date(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Today at 7:37 PM" / "Yesterday at …" / "Wednesday" / "12 Aug 2026". */
export const formatEventTime = (iso) => {
  const date = parseOccurredAt(iso);
  if (!date) return "";
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days <= 0) return `Today at ${time}`;
  if (days === 1) return `Yesterday at ${time}`;
  if (days < 7) return date.toLocaleDateString([], { weekday: "long" });
  return date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
